import { getElement, defineElement } from "./gw.js";

const KLAVIYO_CLIENT_API = "https://a.klaviyo.com/client";
const KLAVIYO_REVISION = "2026-07-15";

class BackInStockDialog extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector("dialog");
    this.form = this.querySelector("[data-back-in-stock-form]");
    this.submitButton = this.form.querySelector('[type="submit"]');
    this.errorMessage = this.querySelector("[data-back-in-stock-error]");
    this.successMessage = this.querySelector("[data-back-in-stock-success]");

    this.form.addEventListener("submit", this.onSubmit.bind(this));
    this.querySelector("[data-back-in-stock-close]").addEventListener(
      "click",
      () => this.dialog.close(),
    );
    this.dialog.addEventListener("click", (event) => {
      if (event.target === this.dialog) this.dialog.close();
    });
  }

  open({ variantId, productTitle, variantTitle, imageSrc }) {
    this.variantId = variantId;
    this.form.reset();
    this.form.hidden = false;
    this.errorMessage.hidden = true;
    this.successMessage.hidden = true;
    this.submitButton.removeAttribute("aria-disabled");

    const image = this.querySelector("[data-back-in-stock-image]");
    image.src = imageSrc;
    image.alt = productTitle;
    image.parentElement.hidden = !imageSrc;
    this.querySelector("[data-back-in-stock-product-title]").textContent =
      productTitle;
    this.querySelector("[data-back-in-stock-variant-title]").textContent =
      variantTitle;

    this.dialog.showModal();
    this.form.elements.email.focus();
  }

  async onSubmit(event) {
    event.preventDefault();
    if (this.submitButton.getAttribute("aria-disabled") === "true") return;

    const emailInput = this.form.elements.email;
    if (!emailInput.checkValidity()) {
      this.errorMessage.hidden = false;
      emailInput.focus();
      return;
    }

    const email = emailInput.value.trim();
    this.errorMessage.hidden = true;
    this.submitButton.setAttribute("aria-disabled", "true");

    const requests = [this.subscribeToVariant(email)];
    if (this.form.elements.newsletter.checked) {
      requests.push(this.subscribeToList(email));
    }

    try {
      await Promise.all(requests);
      this.form.hidden = true;
      this.successMessage.hidden = false;
    } catch (error) {
      console.error(error);
      this.errorMessage.hidden = false;
    } finally {
      this.submitButton.removeAttribute("aria-disabled");
    }
  }

  subscribeToVariant(email) {
    return this.request("back-in-stock-subscriptions", {
      type: "back-in-stock-subscription",
      attributes: {
        channels: ["EMAIL"],
        profile: { data: { type: "profile", attributes: { email } } },
      },
      relationships: {
        variant: {
          data: {
            type: "catalog-variant",
            id: `$shopify:::$default:::${this.variantId}`,
          },
        },
      },
    });
  }

  subscribeToList(email) {
    return this.request("subscriptions", {
      type: "subscription",
      attributes: {
        profile: {
          data: {
            type: "profile",
            attributes: {
              email,
              subscriptions: { email: { marketing: { consent: "SUBSCRIBED" } } },
            },
          },
        },
      },
      relationships: {
        list: { data: { type: "list", id: this.dataset.listId } },
      },
    });
  }

  async request(endpoint, data) {
    const url = `${KLAVIYO_CLIENT_API}/${endpoint}/?company_id=${encodeURIComponent(this.dataset.companyId)}`;
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/vnd.api+json",
        revision: KLAVIYO_REVISION,
      },
      body: JSON.stringify({ data }),
    });
    if (!response.ok) {
      throw new Error(`Klaviyo ${endpoint} request failed: ${response.status}`);
    }
  }
}

if (!getElement("back-in-stock-dialog")) {
  defineElement("back-in-stock-dialog", BackInStockDialog);
}
